// src/utils/session.ts
//
// What "your session is over" means, in one place.
//
// The backend issues a 24-hour JWT (`ACCESS_TOKEN_EXPIRE_MINUTES = 1440`) while
// the FE stores it in a cookie that lives seven days
// (`VITE_AUTH_TOKEN_EXPIRED_DAY`). For six of those days the browser holds a
// token the server has already stopped accepting: `getCookie("authToken")`
// succeeds, `logged_in` is still "true", every route renders — and every API
// call 401s. Nothing was watching for that, so each screen invented its own
// message for it, and a signed-out user looked like a broken feature.
//
// The interceptor in ./authInterceptor is what notices. This module is what it
// does about it, kept separate so the redirect policy is readable on its own
// and so the three existing logout buttons have something to converge on.

import { removeCookie } from "./cookie";
import { readViewAs, VIEW_AS_ENDED_FLAG, VIEW_AS_PLACEHOLDER_FLAG } from "./viewAs";

/**
 * Everything that says "someone is signed in".
 *
 * Taken from the existing logout buttons (`mini-sidebar_v2`, `admin-sidebar`,
 * `header`), which each keep their own slightly different list. Those are left
 * alone here — converging them is worthwhile but it is not this fix, and
 * quietly changing what three buttons clear is how a logout starts leaving
 * something behind.
 */
const SESSION_COOKIES = [
    "authToken",
    "logged_in",
    "__apply_job_guest__",
    "__account_startup_screening__",
];

const SESSION_STORAGE_KEYS = ["cv_uuid", "__INVITE_MEMBER_EMAIL__", VIEW_AS_PLACEHOLDER_FLAG];

/** Where the login screen reads that this was an expiry, not a plain sign-out. */
export const SESSION_EXPIRED_FLAG = "__session_expired__";

/**
 * Pages that are public and carry their OWN credential, so no 401 on them can
 * mean "your session expired" — there is no session to expire (#3498).
 *
 * `/oura/report/:id` is printed by headless Chromium with no cookie, authorised
 * by a short-lived render token in the URL. It is mounted inside the same app
 * shell as everything else, so the shell's authenticated bootstrap (the Oura
 * scan poller, see `oura-processing-toast`) fires there and 401s ~2s after boot.
 * Treating that as an expiry sent `window.location.href = "/"` and destroyed the
 * page mid-render, before its own authorised payload had finished loading — the
 * renderer then waited out its full 90s and every scan finished
 * REPORT_RENDER_FAILED.
 *
 * The exemption is keyed on the PAGE ROUTE, not the request URL: the request
 * that 401s belongs to the app shell, not to the report, so no URL allowlist
 * could ever have caught it.
 */
const SESSIONLESS_ROUTE_PREFIXES = ["/oura/report/"];

export function isSessionlessRoute(pathname: string = window.location.pathname): boolean {
    return SESSIONLESS_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

/** Drop every trace of the signed-in session. Safe to call more than once. */
export function clearSession(): void {
    SESSION_COOKIES.forEach(removeCookie);
    SESSION_STORAGE_KEYS.forEach((k) => sessionStorage.removeItem(k));
}

/**
 * True once a redirect is already on its way.
 *
 * A page usually has several requests in flight, and an expired token fails all
 * of them within milliseconds of each other. Without this latch each one would
 * assign `location.href` and the browser would be handed a burst of navigations
 * to the same place. The flag is module-level and never reset: the redirect
 * tears down this JS context anyway, so "already redirecting" stays true for
 * exactly as long as it needs to be.
 */
let redirecting = false;

/**
 * End the session and send the user to the login screen.
 *
 * The login route is `/` (see `src/routes/index.tsx`). This is a full document
 * navigation rather than a router `navigate()` on purpose — the interceptor
 * lives outside React and has no router handle, and a hard load is also the
 * only way to guarantee no stale authenticated state survives in memory.
 */
export function endExpiredSession(): void {
    if (redirecting) return;
    // Guarded here rather than in the interceptor: this is the point that
    // actually destroys the page, so every present and future caller is covered
    // by one check instead of each remembering the exemption.
    if (isSessionlessRoute()) return;
    redirecting = true;
    // Read before the cookie goes: a view-as session ends on a clock the person
    // did not choose (two hours), and "sign in again" is the wrong instruction
    // for staff, who hold no merchant credentials. They reopen it instead.
    const wasViewAs = readViewAs().active;
    clearSession();
    // Read and cleared by the login screen so it can say WHY the user is here.
    // sessionStorage, not a query param: it must not survive a shared link.
    try {
        sessionStorage.setItem(wasViewAs ? VIEW_AS_ENDED_FLAG : SESSION_EXPIRED_FLAG, "1");
    } catch {
        // Private-mode quota failures must not stop the sign-out itself.
    }
    window.location.href = "/";
}
